import { computed, reactive, ref, watch } from 'vue';
import { cloneDeep, merge } from 'lodash';
import type { Feature } from 'ol';
import { GeoJSON } from 'ol/format';
import VectorImageLayer from 'ol/layer/VectorImage';
import { Vector } from 'ol/source';
import type {
  VectorLayerZone,
  VectorLayerZoneStyleData,
  VectorMapLayer,
  Map,
  VectorMapLayerConfig,
  VectorMapLayerGroupConfig,
  VectorMapLayerGroup
} from '../models';
import { MapCalculationMode, MapLayerType } from '../enums';
import {
  applyFontSizeDependingOnResolution,
  defaultMapInfoSubTitleFn,
  defaultMapInfoTitleFn,
  defaultMapInfoValueFn,
  defaultZoneStyleFn,
  emptyZoneLabelFn
} from '../utils';
import { useMap, useMapInfo, useMapLayer } from '../composables';
import { Geometry } from 'ol/geom';

export const createVectorMapLayer = (config: VectorMapLayerConfig): VectorMapLayer => {
  const zoneStyleConfig = config.zoneStyleConfig;
  const zoneStyleFn = config.zoneStyleFn !== undefined ? config.zoneStyleFn : defaultZoneStyleFn;
  const zoneLabelFn = config.zoneLabelFn !== undefined ? config.zoneLabelFn : emptyZoneLabelFn;

  const layer: VectorMapLayer = {
    mapLayerType: MapLayerType.VECTOR,
    type: config.type,
    id: config.id,
    name: config.name,
    data: {
      classification: config.classification,
      unitType: config.unitType,
      unitTypeName: config.unitTypeName,
      colorMap: config.colorMap,
      values: [],
      zones: [],
      features: [],
      zoneStyleConfig: {
        defaultStyle: merge({}, zoneStyleConfig?.defaultStyle || {}),
        selectedStyle: merge({}, zoneStyleConfig?.defaultStyle || {}, zoneStyleConfig?.selectedStyle || {})
      },
      zoneStyleFn,
      zoneLabelFn,
      lineStyleFn: config.lineStyleFn
    },
    enabled: config.enabled !== false,
    opacity: Math.min(Math.max(config.opacity === undefined ? 1 : config.opacity, 0), 1),
    extent: config.extent || [],
    loading: true,
    customData: config.customData,
    generateColorInfo: config.generateColorInfo,
    mapInfo: {
      doNotGenerate: config.mapInfo?.doNotGenerate === true,
      enabled: config.mapInfo?.enabled !== false,
      titleFn: config.mapInfo?.titleFn !== undefined ? config.mapInfo.titleFn : defaultMapInfoTitleFn,
      subtitleFn: config.mapInfo?.subtitleFn !== undefined ? config.mapInfo.subtitleFn : defaultMapInfoSubTitleFn,
      valueFn: config.mapInfo?.valueFn !== undefined ? config.mapInfo.valueFn : defaultMapInfoValueFn,
      colorBar: config.mapInfo?.colorBar === true
    }
  };

  layer.loadingPromise = new Promise((resolve) => {
    const { reactiveLayer } = useVectorMapLayer(layer);
    calculateVectorMapLayerData(reactiveLayer as VectorMapLayer, config);
    reactiveLayer.loading = false;
    resolve();
  });

  return layer;
};

const calculateVectorMapLayerData = (layer: VectorMapLayer, config: VectorMapLayerConfig) => {
  const geoJsonFormat = new GeoJSON();
  const rawData = config.rawData;

  const zones: VectorLayerZone[] = [];
  const features = geoJsonFormat.readFeatures(rawData, {
    featureProjection: config.featureProjection,
    dataProjection: config.dataProjection
  }) as Feature<Geometry>[];

  features.forEach((feature, i) => {
    const index = feature.get('index') || i + 1;

    const zone: VectorLayerZone = {
      id: feature.getId() as string,
      index,
      name: `#${index}`,
      value: feature.get('value') || 0,
      area: feature.get('area') || 0,
      customData: feature.get('zoneData') || {},
      selected: false
    };

    feature.set('zone', zone);
    zones.push(zone);
  });

  const values = zones.map((zone) => zone.value);

  layer.data.zones = zones;
  layer.data.features = features;
  layer.data.values = values;
};

export const useVectorMapLayer = (layer: VectorMapLayer) => {
  const composedLayer = useMapLayer(layer);
  const reactiveLayer = composedLayer.reactiveLayer as VectorMapLayer;

  const zones = computed(() => reactiveLayer.data.zones);
  const features = computed(() => reactiveLayer.data.features);

  const getZone = (zoneId: string) => {
    return zones.value.find((zone) => zone.id === zoneId);
  };

  const selectZone = (zoneId: string) => {
    const zone = getZone(zoneId);
    if (!zone) return;

    zone.selected = true;
  };

  const deselectZone = (zoneId: string) => {
    const zone = getZone(zoneId);
    if (!zone) return;

    zone.selected = false;
  };

  const deselectAllZones = () => {
    zones.value.forEach((zone) => (zone.selected = false));
  };

  return {
    ...composedLayer,
    reactiveLayer,
    zones,
    features,
    getZone,
    selectZone,
    deselectZone,
    deselectAllZones
  };
};

export const useVectorMapLayerComponent = (layer: VectorMapLayer) => {
  const geoJsonFormat = new GeoJSON();

  const composedLayer = useVectorMapLayer(layer);
  const { reactiveLayer, enabled, features } = composedLayer;
  const {
    calculationMode,
    reactiveMap,
    updateMapInfos,
    getMergedValuesByLayerType,
    getMapInfoByLayerId,
    updateZIndexes
  } = useMap(reactiveLayer.group?.map as Map);

  const layerRef = ref<any>(null);
  const vectorSourceRef = ref<any>(null);

  const getVectorLayer = () => {
    return layerRef.value?.vectorImageLayer as VectorImageLayer<any>;
  };

  const getVectorSource = () => {
    return vectorSourceRef.value?.source as Vector;
  };

  const updateLayerStyle = async () => {
    if (!layerRef.value || !enabled.value) return;

    const vectorSource = getVectorSource();
    vectorSource.clear();

    const values =
      calculationMode.value === MapCalculationMode.ISOLATED
        ? reactiveLayer.data.values
        : getMergedValuesByLayerType(reactiveLayer.type);

    if (reactiveLayer.data.lineStyleFn) {
      const generatedStyleFunction = reactiveLayer.data.lineStyleFn(reactiveLayer, values);
      features.value.forEach((feature) => {
        const lineStyles = generatedStyleFunction(feature);
        feature.setStyle(lineStyles);
      });
    } else {
      const styles: VectorLayerZoneStyleData[] = await reactiveLayer.data.zoneStyleFn(
        reactiveLayer as VectorMapLayer,
        values
      );

      features.value.forEach((feature, i) => {
        feature.setStyle((_: any, resolution: number) => {
          const zone = feature.get('zone');
          const style = zone.selected ? styles[i].selectedStyle : styles[i].defaultStyle;

          if (style) {
            const clonedStyle = cloneDeep(style);
            applyFontSizeDependingOnResolution(style, clonedStyle, resolution);
            return clonedStyle;
          }

          return style;
        });
      });
    }

    vectorSource.addFeatures(features.value as Feature[]);
  };

  const updateLayerOpacity = () => {
    if (!layerRef.value || !enabled.value) return;

    const vectorLayer = getVectorLayer();
    vectorLayer.setOpacity(reactiveLayer.opacity);
  };

  const updateZIndex = () => {
    if (!layerRef.value || !enabled.value) return;

    const vectorLayer = getVectorLayer();
    vectorLayer.setZIndex(reactiveLayer.group?.zIndex || 0);
  };

  watch(calculationMode, async () => {
    await updateLayerStyle();
    await updateMapInfos();
    updateZIndex();
  });

  watch(
    () => reactiveLayer.data.zones,
    async () => {
      await updateLayerStyle();
    },
    { deep: true }
  );

  watch(
    () => reactiveLayer.data.colorMap?.breaks,
    async () => {
      await updateLayerStyle();
      await updateMapInfos();
    },
    { deep: true }
  );

  watch(
    () => reactiveLayer.data.zoneLabelFn,
    async () => {
      await updateLayerStyle();
    },
    { deep: true }
  );

  watch(
    () => reactiveLayer.opacity,
    () => {
      updateLayerOpacity();
      updateZIndex();
    }
  );

  watch(
    () => layerRef.value,
    async () => {
      await updateLayerStyle();
      await updateMapInfos();
      updateLayerOpacity();
      updateZIndex();

      if (layerRef.value && !reactiveMap.firstMapLayerLoaded) {
        reactiveMap.firstMapLayerLoaded = true;
      }
    }
  );

  watch(
    () => reactiveLayer.enabled,
    (enabled) => {
      const mapInfo = getMapInfoByLayerId(reactiveLayer.id);
      if (mapInfo) {
        const composedMapInfo = useMapInfo(mapInfo);
        composedMapInfo.enabled.value = enabled;
      }

      updateZIndexes();
      updateZIndex();
    }
  );

  watch(
    () => reactiveLayer.group?.zIndex || 0,
    () => {
      updateZIndex();
    }
  );

  return {
    ...composedLayer,
    geoJsonFormat,
    layerRef,
    vectorSourceRef
  };
};

export const createVectorMapLayerGroup = (config: VectorMapLayerGroupConfig): VectorMapLayerGroup => {
  const subLayers: VectorMapLayer[] = [];

  if (config.mapLayerConfigs && config.mapLayerConfigs.length) {
    config.mapLayerConfigs.forEach((mapLayerConfig) => {
      subLayers.push(createVectorMapLayer(mapLayerConfig));
    });
  }

  const layerGroup: VectorMapLayerGroup = {
    mapLayerType: MapLayerType.VECTOR_GROUP,
    id: config.id,
    name: config.name,
    subLayers,
    activeSubLayer: subLayers[0],
    customData: config.customData,
    zIndex: 0
  };

  subLayers.forEach((subLayer) => (subLayer.group = layerGroup));

  return layerGroup;
};

export const useVectorMapLayerGroup = (layerGroup: VectorMapLayerGroup) => {
  const reactiveLayerGroup = reactive(layerGroup);

  const activeSubLayer = computed(() => reactiveLayerGroup.activeSubLayer);

  const addSubLayer = (subLayer: VectorMapLayer) => {
    subLayer.group = layerGroup;
    reactiveLayerGroup.subLayers.push(subLayer);
  };

  const getSubLayer = (subLayerId: string) => {
    return reactiveLayerGroup.subLayers.find((subLayer) => subLayer.id === subLayerId);
  };

  const switchSubLayer = (subLayerId: string) => {
    const subLayer = getSubLayer(subLayerId);

    if (subLayer) {
      reactiveLayerGroup.activeSubLayer = subLayer;
    }
  };

  return {
    reactiveLayerGroup,
    activeSubLayer,
    addSubLayer,
    getSubLayer,
    switchSubLayer
  };
};
